import { PotentialDuplicatePair } from "@ac-expo/api-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import { potentialDuplicateEventsQueryKeys } from "../query-keys/potential-duplicate-events.query-keys";
import { acExpoEventsQueryKeys } from "../query-keys/events.query-keys";

const deleteEventFromPotentialDuplicate = async (
  eventId: string,
  duplicateId: string
) => {
  const { data } = await privateAxios.delete(
    `/events/automated-events-to-control/potential-duplicate/${eventId}/${duplicateId}`
  );
  return data;
};

export const useDeleteEventFromDuplicateControl = () => {
  return useMutation({
    mutationFn: ({
      eventId,
      duplicateId,
    }: {
      eventId: string;
      duplicateId: string;
    }) => deleteEventFromPotentialDuplicate(eventId, duplicateId),
  });
};

const publishPotentialDuplicates = async (duplicateIds: string[]) => {
  const { data } = await privateAxios.post(
    `/events/automated-events-to-control/potential-duplicate/publish-events`,
    {
      duplicateIds,
    }
  );

  return data;
};

export const usePublishPotentialDuplicates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ duplicateIds }: { duplicateIds: string[] }) =>
      publishPotentialDuplicates(duplicateIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: acExpoEventsQueryKeys.list(),
      });
      queryClient.invalidateQueries({
        queryKey: potentialDuplicateEventsQueryKeys.all,
      });
    },
  });
};

const deletePotentialDuplicates = async (duplicateIds: string[]) => {
  const { data } = await privateAxios.delete(
    `/events/automated-events-to-control/potential-duplicate/delete-events`,
    {
      data: {
        duplicateIds,
      },
    }
  );
  return data;
};

export const useDeletePotentialDuplicates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ duplicateIds }: { duplicateIds: string[] }) =>
      deletePotentialDuplicates(duplicateIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: potentialDuplicateEventsQueryKeys.all,
      });
    },
  });
};

const deleteAllPotentialDuplicatesPairs = async () => {
  const { data } = await privateAxios.delete(
    `/events/automated-events-to-control/potential-duplicate/delete-all-pairs`
  );
  return data;
};

export const useDeleteAllPotentialDuplicatesPairs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteAllPotentialDuplicatesPairs(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: acExpoEventsQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: potentialDuplicateEventsQueryKeys.all,
      });
    },
  });
};

type ProcessPotentialDuplicatesArgs = {
  duplicateIdsToPublish: string[];
  duplicateIdsToDelete: string[];
  potentialDuplicatePairs: PotentialDuplicatePair[];
};
const processPotentialDuplicates = async (
  duplicateIdsToPublish: string[],
  duplicateIdsToDelete: string[],
  potentialDuplicatePairs: PotentialDuplicatePair[]
) => {
  const { data } = await privateAxios.post(
    `/events/automated-events-to-control/potential-duplicate/process`,
    {
      duplicateIdsToPublish,
      duplicateIdsToDelete,
      potentialDuplicatePairs,
    }
  );
  return data;
};

export const useProcessPotentialDuplicates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      duplicateIdsToPublish,
      duplicateIdsToDelete,
      potentialDuplicatePairs,
    }: ProcessPotentialDuplicatesArgs) =>
      processPotentialDuplicates(
        duplicateIdsToPublish,
        duplicateIdsToDelete,
        potentialDuplicatePairs
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: potentialDuplicateEventsQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: acExpoEventsQueryKeys.list(),
      });
    },
  });
};
